import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Instructor } from "@features/events/types";

export interface InstructorValidationErrors {
  title?: string;
  firstname?: string;
  lastname?: string;
  headline?: string;
  bio?: string;
  slug?: string;
  languageCodes?: string;
  categoryIds?: string;
  geoAddress?: string;
  email?: string;
  phone?: string;
  phoneCountryCode?: string;
  [key: string]: string | undefined; // For indexed errors like social.0.url, media.0.type
}

export interface TouchedFields {
  firstname?: boolean;
  lastname?: boolean;
  headline?: boolean;
  bio?: boolean;
  slug?: boolean;
  languageCodes?: boolean;
  categoryIds?: boolean;
  geoAddress?: boolean;
}

export function useInstructorValidation() {
  const { t } = useTranslation();
  const [errors, setErrors] = useState<InstructorValidationErrors>({});
  const MAX_INSTRUCTOR_LANGUAGES = 10;

  const validate = useCallback(
    (
      formData: Partial<Instructor>,
      selectedLanguages: string[]
    ): boolean => {
      const newErrors: InstructorValidationErrors = {};

      // Title validation (optional, but if provided must be valid)
      // No validation needed as it's nullable in API

      // Firstname validation - all selected languages must be filled
      if (selectedLanguages.length > 0) {
        const firstname = formData.firstname ?? {};
        const missingLanguages = selectedLanguages.filter(
          (lang) => !firstname[lang]?.trim()
        );
        if (missingLanguages.length > 0) {
          newErrors.firstname = t("common.requiredField", {
            field_name: t("instructorDetails.overview.firstname"),
          });
        }
      } else {
        // Fallback: check if any language has content
        if (
          !formData.firstname ||
          !Object.values(formData.firstname).some((value) => value?.trim())
        ) {
          newErrors.firstname = t("common.requiredField", {
            field_name: t("instructorDetails.overview.firstname"),
          });
        }
      }

      // Lastname validation - all selected languages must be filled
      if (selectedLanguages.length > 0) {
        const lastname = formData.lastname ?? {};
        const missingLanguages = selectedLanguages.filter(
          (lang) => !lastname[lang]?.trim()
        );
        if (missingLanguages.length > 0) {
          newErrors.lastname = t("common.requiredField", {
            field_name: t("instructorDetails.overview.lastname"),
          });
        }
      } else {
        // Fallback: check if any language has content
        if (
          !formData.lastname ||
          !Object.values(formData.lastname).some((value) => value?.trim())
        ) {
          newErrors.lastname = t("common.requiredField", {
            field_name: t("instructorDetails.overview.lastname"),
          });
        }
      }

      // Headline validation - all selected languages must be filled (REQUIRED)
      if (selectedLanguages.length > 0) {
        const headline = formData.headline ?? {};
        const missingLanguages = selectedLanguages.filter(
          (lang) => !headline[lang]?.trim()
        );
        if (missingLanguages.length > 0) {
          newErrors.headline = t("common.requiredField", {
            field_name: t("instructorDetails.overview.headline"),
          });
        }
      } else {
        // Fallback: check if any language has content
        if (
          !formData.headline ||
          !Object.values(formData.headline).some((value) => value?.trim())
        ) {
          newErrors.headline = t("common.requiredField", {
            field_name: t("instructorDetails.overview.headline"),
          });
        }
      }

      // Bio validation - all selected languages must be filled (REQUIRED)
      if (selectedLanguages.length > 0) {
        const bio = formData.bio ?? {};
        const missingLanguages = selectedLanguages.filter(
          (lang) => !bio[lang]?.trim()
        );
        if (missingLanguages.length > 0) {
          newErrors.bio = t("common.requiredField", {
            field_name: t("instructorDetails.overview.bio.label"),
          });
        }
      } else {
        // Fallback: check if any language has content
        if (
          !formData.bio ||
          !Object.values(formData.bio).some((value) => value?.trim())
        ) {
          newErrors.bio = t("common.requiredField", {
            field_name: t("instructorDetails.overview.bio.label"),
          });
        }
      }

      // Slug validation (required)
      if (!formData.slug || !formData.slug.trim()) {
        newErrors.slug = t("common.requiredField", {
          field_name: t("instructorDetails.overview.slug"),
        });
      }

      // Language codes validation (required, min: 1, max: 10)
      if (
        !formData.languageCodes ||
        formData.languageCodes.length === 0
      ) {
        newErrors.languageCodes = t("common.requiredField", {
          field_name: t("instructorDetails.languages.select.title"),
        });
      } else if (formData.languageCodes.length > MAX_INSTRUCTOR_LANGUAGES) {
        newErrors.languageCodes = t("common.validation.maxItemsError", {
          field_name: t("instructorDetails.languages.select.title"),
          max: MAX_INSTRUCTOR_LANGUAGES,
          defaultValue: `${t(
            "instructorDetails.languages.select.title"
          )} can have at most ${MAX_INSTRUCTOR_LANGUAGES} items.`,
        });
      }

      // Category IDs validation (required, min: 1, max: 4)
      if (!formData.categoryIds || formData.categoryIds.length === 0) {
        newErrors.categoryIds = t("common.requiredField", {
          field_name: t("instructorDetails.topics.select.title"),
        });
      } else if (formData.categoryIds.length > 4) {
        newErrors.categoryIds = t("common.validation.maxItemsError", {
          field_name: t("instructorDetails.topics.select.title"),
          max: 4,
          defaultValue: `${t(
            "instructorDetails.topics.select.title"
          )} can have at most 4 items.`,
        });
      }

      // GeoAddress validation - check if googlePlaceId is provided (required)
      if (
        !formData.geoAddress ||
        !formData.geoAddress.googlePlaceId ||
        !formData.geoAddress.googlePlaceId.trim()
      ) {
        newErrors.geoAddress = t("common.requiredField", {
          field_name: t("instructorDetails.contactInformation.primaryAddress"),
        });
      }

      // Email validation (optional, but if provided must be valid email)
      if (formData.email && formData.email.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email.trim())) {
          newErrors.email = t("common.validation.invalidEmail", {
            defaultValue: "Please enter a valid email address",
          });
        }
      }

      // Phone validation (optional, but if provided should be valid)
      // Phone validation is handled by PhoneNumberField component

      // Social media validation (required per item when social array exists)
      if (formData.social && formData.social.length > 0) {
        formData.social.forEach((socialItem, index) => {
          if (!socialItem.url || !socialItem.url.trim()) {
            newErrors[`social.${index}.url`] = t("common.requiredField", {
              field_name: `Social ${index + 1} URL`,
            });
          }
          if (!socialItem.social || !socialItem.social.trim()) {
            newErrors[`social.${index}.social`] = t("common.requiredField", {
              field_name: `Social ${index + 1} Platform`,
            });
          }
        });
      }

      // Media validation (required per item when media array exists)
      if (formData.media && formData.media.length > 0) {
        formData.media.forEach((mediaItem, index) => {
          if (
            !mediaItem.type ||
            (mediaItem.type !== "img" && mediaItem.type !== "vid")
          ) {
            newErrors[`media.${index}.type`] = t("common.requiredField", {
              field_name: `Media ${index + 1} Type`,
            });
          }
          if (!mediaItem.source) {
            newErrors[`media.${index}.source`] = t("common.requiredField", {
              field_name: `Media ${index + 1} Source`,
            });
          }
        });
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    },
    [t]
  );

  // Validate a single field for real-time validation
  const validateField = useCallback(
    (
      fieldName: string,
      value: unknown,
      _formData: Partial<Instructor>,
      selectedLanguages: string[]
    ): string | undefined => {
      switch (fieldName) {
        case "firstname": {
          const firstname = value as Record<string, string> | undefined;
          // Required field: all selected languages must be filled
          // Note: "required_with" validation (if any language has content, all must have content)
          // is handled by GenerativeTextField when validateAllLanguages={true}
          if (selectedLanguages.length > 0) {
            const missingLanguages = selectedLanguages.filter(
              (lang) => !firstname?.[lang]?.trim()
            );
            if (missingLanguages.length > 0) {
              return t("common.requiredField", {
                field_name: t("instructorDetails.overview.firstname"),
              });
            }
          } else {
            if (
              !firstname ||
              !Object.values(firstname).some((v) => v?.trim())
            ) {
              return t("common.requiredField", {
                field_name: t("instructorDetails.overview.firstname"),
              });
            }
          }
          return undefined;
        }
        case "lastname": {
          const lastname = value as Record<string, string> | undefined;
          // Required field: all selected languages must be filled
          // Note: "required_with" validation (if any language has content, all must have content)
          // is handled by GenerativeTextField when validateAllLanguages={true}
          if (selectedLanguages.length > 0) {
            const missingLanguages = selectedLanguages.filter(
              (lang) => !lastname?.[lang]?.trim()
            );
            if (missingLanguages.length > 0) {
              return t("common.requiredField", {
                field_name: t("instructorDetails.overview.lastname"),
              });
            }
          } else {
            if (!lastname || !Object.values(lastname).some((v) => v?.trim())) {
              return t("common.requiredField", {
                field_name: t("instructorDetails.overview.lastname"),
              });
            }
          }
          return undefined;
        }
        case "headline": {
          const headline = value as Record<string, string> | undefined;
          if (selectedLanguages.length > 0) {
            const missingLanguages = selectedLanguages.filter(
              (lang) => !headline?.[lang]?.trim()
            );
            if (missingLanguages.length > 0) {
              return t("common.requiredField", {
                field_name: t("instructorDetails.overview.headline"),
              });
            }
          } else {
            if (!headline || !Object.values(headline).some((v) => v?.trim())) {
              return t("common.requiredField", {
                field_name: t("instructorDetails.overview.headline"),
              });
            }
          }
          return undefined;
        }
        case "bio": {
          const bio = value as Record<string, string> | undefined;
          if (selectedLanguages.length > 0) {
            const missingLanguages = selectedLanguages.filter(
              (lang) => !bio?.[lang]?.trim()
            );
            if (missingLanguages.length > 0) {
              return t("common.requiredField", {
                field_name: t("instructorDetails.overview.bio.label"),
              });
            }
          } else {
            if (!bio || !Object.values(bio).some((v) => v?.trim())) {
              return t("common.requiredField", {
                field_name: t("instructorDetails.overview.bio.label"),
              });
            }
          }
          return undefined;
        }
        case "slug": {
          const slug = value as string | undefined;
          if (!slug || !slug.trim()) {
            return t("common.requiredField", {
              field_name: t("instructorDetails.overview.slug"),
            });
          }
          return undefined;
        }
        case "languageCodes": {
          const languageCodes = value as string[] | undefined;
          if (!languageCodes || languageCodes.length === 0) {
            return t("common.requiredField", {
              field_name: t("instructorDetails.languages.select.title"),
            });
          }
          if (languageCodes.length > MAX_INSTRUCTOR_LANGUAGES) {
            return t("common.validation.maxItemsError", {
              field_name: t("instructorDetails.languages.select.title"),
              max: MAX_INSTRUCTOR_LANGUAGES,
              defaultValue: `${t(
                "instructorDetails.languages.select.title"
              )} can have at most ${MAX_INSTRUCTOR_LANGUAGES} items.`,
            });
          }
          return undefined;
        }
        case "categoryIds": {
          const categoryIds = value as string[] | undefined;
          if (!categoryIds || categoryIds.length === 0) {
            return t("common.requiredField", {
              field_name: t("instructorDetails.topics.select.title"),
            });
          }
          if (categoryIds.length > 4) {
            return t("common.validation.maxItemsError", {
              field_name: t("instructorDetails.topics.select.title"),
              max: 4,
              defaultValue: `${t(
                "instructorDetails.topics.select.title"
              )} can have at most 4 items.`,
            });
          }
          return undefined;
        }
        case "geoAddress": {
          const geoAddress = value as Instructor["geoAddress"] | undefined;
          if (
            !geoAddress ||
            !geoAddress.googlePlaceId ||
            !geoAddress.googlePlaceId.trim()
          ) {
            return t("common.requiredField", {
              field_name: t("instructorDetails.contactInformation.primaryAddress"),
            });
          }
          return undefined;
        }
        case "email": {
          const email = value as string | undefined;
          if (email && email.trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email.trim())) {
              return t("common.validation.invalidEmail", {
                defaultValue: "Please enter a valid email address",
              });
            }
          }
          return undefined;
        }
        case "social": {
          const social = value as Instructor["social"] | undefined;
          if (social && social.length > 0) {
            // Validate all social items
            for (let index = 0; index < social.length; index++) {
              const socialItem = social[index];
              if (!socialItem.url || !socialItem.url.trim()) {
                return t("common.requiredField", {
                  field_name: `Social ${index + 1} URL`,
                });
              }
              if (!socialItem.social || !socialItem.social.trim()) {
                return t("common.requiredField", {
                  field_name: `Social ${index + 1} Platform`,
                });
              }
            }
          }
          return undefined;
        }
        case "media": {
          const media = value as Instructor["media"] | undefined;
          if (media && media.length > 0) {
            // Validate all media items
            for (let index = 0; index < media.length; index++) {
              const mediaItem = media[index];
              if (
                !mediaItem.type ||
                (mediaItem.type !== "img" && mediaItem.type !== "vid")
              ) {
                return t("common.requiredField", {
                  field_name: `Media ${index + 1} Type`,
                });
              }
              if (!mediaItem.source) {
                return t("common.requiredField", {
                  field_name: `Media ${index + 1} Source`,
                });
              }
            }
          }
          return undefined;
        }
        default:
          return undefined;
      }
    },
    [t]
  );

  // Update error for a specific field
  const setFieldError = useCallback(
    (fieldName: string, error: string | undefined) => {
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (error) {
          newErrors[fieldName] = error;
        } else {
          delete newErrors[fieldName];
        }
        // Also clear indexed errors for this field (e.g., social.0.url)
        Object.keys(newErrors).forEach((key) => {
          if (key.startsWith(`${fieldName}.`)) {
            delete newErrors[key];
          }
        });
        return newErrors;
      });
    },
    []
  );

  // Clear error for a specific field
  const clearFieldError = useCallback((fieldName: string) => {
    setFieldError(fieldName, undefined);
  }, [setFieldError]);

  const clearErrors = useCallback(() => setErrors({}), []);

  return {
    errors,
    validate,
    validateField,
    setFieldError,
    clearFieldError,
    clearErrors,
  };
}
